/* eslint-disable max-len */
import {type ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Controller, useForm} from 'react-hook-form';
import classNames from 'classnames';
import {useId} from '@react-aria/utils';
import {useInterval} from 'usehooks-ts';

import {sendToStatist} from '@time-webkit/statist';
import {Select} from '@time-webkit/all/atoms/select';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';
import {Button, BUTTON_VARIANT} from '@time-webkit/all/atoms/button';
import {InputChannels} from '@time-webkit/all/organisms/input-channels';
import {FormError} from '@time-webkit/all/atoms/form-error';
import {Link} from '@time-webkit/all/atoms/link';
import {Alert, AlertType} from '@time-webkit/all/atoms/alert';
import {type Team, type TeamInvitationRole} from '@mattermost/types/teams';
import {type Channel} from '@mattermost/types/channels';

import {CopyEmailsButton} from '../copy_emails_button';
import {inviteMembersContext} from '../invite_members_context';
import {isAnyTeamGuestRole} from '../utils';
import {
    AUTOCOMPLETE_CHANNELS_DEFAULT,
    DELAY_MS,
    EMAIL_MAX_LENGTH,
    EMAILS_LIMIT_DEFAULT,
    INITIAL_CHANNELS,
    ROLES_MAP,
} from '../consts';

import {ChannelFieldErrors, ChannelsErrorType} from './channel_field_errors';
import {InputInviteByEmail} from './input-invite-by-email';
import styles from './invite_members_form.module.css';
import {ChannelFieldInvitationErrorCode} from './channel_field_invitation_error_code';

type Props = {
    archivedChannelsMap: Record<string, Channel>;
    canAddMembers: boolean;
    canInviteGuests: boolean;
    canInviteGuestsPlus: boolean;
    containerId: string;
    team: Team;
    role: TeamInvitationRole;
    channels?: Channel[];
    emailMaxLength?: number;
    autocompleteChannels?: Channel[];
    emailsLimit?: number;
    guestChannelsLimit: number;
    guestPlusChannelsLimit: number;
    allowedCurrentTeamDomains: string[];
    allowedGuestDomains?: string[];
    allowedTeamDomains?: string[];
    renderInviteMemberLink?: () => ReactNode;
    onCancel?: () => void;
}

type InviteMembersFormFields = {
    emails: string[];
    role: TeamInvitationRole;
    channels: Channel[];
}

export const InviteMembersForm =
    (
        {
            archivedChannelsMap,
            canAddMembers,
            canInviteGuests,
            canInviteGuestsPlus,
            containerId,
            team,
            role: roleProp,
            channels: initialChannels = INITIAL_CHANNELS,
            emailMaxLength = EMAIL_MAX_LENGTH,
            autocompleteChannels = AUTOCOMPLETE_CHANNELS_DEFAULT,
            emailsLimit = EMAILS_LIMIT_DEFAULT,
            guestChannelsLimit,
            guestPlusChannelsLimit,
            allowedGuestDomains,
            allowedCurrentTeamDomains,
            allowedTeamDomains,
            renderInviteMemberLink,
            onCancel,
        }: Props,
    ) => {
        const intl = useIntl();
        const inviteByEmailErrorId = useId();
        const inviteByEmailWarningId = useId();
        const channelsErrorId = useId();
        const {
            rateLimitTimeoutMinutes,
            result,
            criticalErrors,
            rateLimitIssues,
            profiles,
            warnings,
            errors: invitationErrors,
            channelError,
            generalError,
            reset,
            sendInvites,
            failedToSendInvites,
        } = useContext(inviteMembersContext);
        const [emailsLimitExcess, setEmailsLimitExcess] = useState(0);
        const [rateLimitIssueTimeout, setRateLimitIssueTimeout] = useState(rateLimitTimeoutMinutes);

        const {roleOptions, initialRole} = useMemo(() => {
            const availableRoles: TeamInvitationRole[] = [
                ...(canAddMembers ? ['team_user'] as const : []),
                ...(canInviteGuestsPlus ? ['team_guest'] as const : []),
                ...(canInviteGuests ? ['team_restricted_guest'] as const : []),
            ];

            const initialRole = availableRoles.includes(roleProp) ? roleProp : availableRoles[0];

            const roleOptions = availableRoles.map((role) => ({
                value: role,
                label: intl.formatMessage({id: ROLES_MAP[role].displayName, defaultMessage: ROLES_MAP[role].name}),
            }));

            return {roleOptions, initialRole};

            // По требованиям реагировать на изменение прав не нужно. Опции в списке должны меняться только при повторном открытии окна
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const {control, watch, handleSubmit, getValues, setValue, trigger, formState} = useForm<InviteMembersFormFields>({
            defaultValues: {
                emails: [],
                role: initialRole,
                channels: initialChannels,
            },
            mode: 'onChange',
        });
        const selectedRole = watch('role');
        const isDirtyChannelsField = formState.dirtyFields.channels;
        const isValidForm = formState.isValid;

        const [bodyMTrueTypography, bodyMTypography] = useTypography([{size: TypographySize.BodyMTrue}, {size: TypographySize.BodyM}]);

        useInterval(
            () => {
                setRateLimitIssueTimeout(rateLimitIssueTimeout - 1);
            },
            rateLimitIssueTimeout > 0 ? DELAY_MS : null,
        );

        const channelsLimit = selectedRole === 'team_restricted_guest' ? guestChannelsLimit : guestPlusChannelsLimit;
        const isArchiveChannelsFoundError = channelError?.code === ChannelFieldInvitationErrorCode.API_ARCHIVED_CHANNELS_FOUND;

        const handleDeleteInvalidEmails = useCallback(() => {
            const {emails} = getValues();
            const validEmails = emails.filter((email) => !criticalErrors.find((error) => error.email === email));
            setValue('emails', validEmails);
        }, [criticalErrors, getValues, setValue]);

        const withdrawEmailsLimitExcess = useCallback(() => {
            setEmailsLimitExcess(0);
        }, [setEmailsLimitExcess]);

        const submitForm = (formFields: InviteMembersFormFields) => {
            return sendInvites({...formFields, team});
        };

        useEffect(() => {
            setRateLimitIssueTimeout(rateLimitTimeoutMinutes);
        }, [rateLimitTimeoutMinutes]);

        useEffect(() => {
            const subscription = watch((data) => {
                const {emails, role, channels} = data as unknown as InviteMembersFormFields;
                if (emails.length === 0 || (isAnyTeamGuestRole(role) && channels.length === 0)) {
                    reset();
                    return;
                }
                sendInvites({
                    emails,
                    role,
                    channels,
                    team,
                    dryRun: true,
                });
            });
            return () => subscription.unsubscribe();
        }, [team, reset, sendInvites, watch]);

        useEffect(() => {
            if (isDirtyChannelsField && isAnyTeamGuestRole(selectedRole)) {
                trigger('channels');
            }
        }, [isDirtyChannelsField, selectedRole, trigger]);

        const renderWarnings = (content: string) => (
            <div
                id={inviteByEmailWarningId}
                className={classNames(styles.inputDescription, bodyMTypography)}
            >
                {content}
            </div>
        );

        return (
            <form
                data-test-id='InviteMembersForm'
                action='#'
                noValidate={true}
                onSubmit={handleSubmit(submitForm)}
                aria-label={intl.formatMessage({
                    id: 'invite.members.invite_members',
                    defaultMessage: 'Invite to the {team}',
                }, {team: team.display_name})}
            >
                <div className={styles.inputEmailsWrapper}>
                    <Controller
                        name='emails'
                        control={control}
                        rules={{
                            validate: (value) => value.length > 0,
                        }}
                        render={({field}) => {
                            const error = Boolean(formState.errors.emails) || criticalErrors.length > 0;

                            return (
                                <>
                                    <InputInviteByEmail
                                        data-test-id='InputInviteByEmail'
                                        tags={field.value}
                                        limit={emailsLimit}
                                        placeholder={intl.formatMessage({
                                            id: 'invite.members.members_placeholder',
                                            defaultMessage: 'You can add to {count} {count, plural, one {email} other {emails}}',
                                        }, {count: emailsLimit})}
                                        label={intl.formatMessage({
                                            id: 'invite.members.members_label',
                                            defaultMessage: 'User emails',
                                        })}
                                        profiles={profiles}
                                        invitationErrors={invitationErrors}
                                        error={error}
                                        autoFocus={true}
                                        maxLength={emailMaxLength}
                                        onChange={(value) => {
                                            const delta = Math.max(value.length - emailsLimit, 0);
                                            const limitedValue = value.slice(0, value.length - delta);

                                            setEmailsLimitExcess(delta);

                                            if (delta > 0) {
                                                sendToStatist('team.invite.listLimit', {
                                                    teamid: team.id,
                                                    userCount: value.length,
                                                });
                                            }
                                            field.onChange(limitedValue);
                                        }}
                                        onBlur={field.onBlur}
                                        allowedCurrentTeamDomains={allowedCurrentTeamDomains}
                                        allowedSystemDomains={isAnyTeamGuestRole(selectedRole) ? allowedGuestDomains : allowedTeamDomains}
                                        aria-errormessage={inviteByEmailErrorId}
                                        aria-describedby={inviteByEmailWarningId}
                                    />
                                    {
                                        emailsLimit && field.value.length >= emailsLimit && !error && (
                                            renderWarnings(intl.formatMessage({
                                                id: 'invite.members.limit_reached_description',
                                                defaultMessage: 'You have added the maximum number of users',
                                            }))
                                        )
                                    }
                                </>
                            );
                        }}
                    />
                    <FormError
                        id={inviteByEmailErrorId}
                        className={styles.error}
                        show={Boolean(formState.errors.emails && criticalErrors.length === 0)}
                        message={intl.formatMessage(
                            {id: 'invite.members.no_emails_error', defaultMessage: 'Add up to {count} users'},
                            {count: emailsLimit},
                        )}
                    />
                    {
                        criticalErrors.length > 0 && (
                            <div className={styles.errorWithAction}>
                                <FormError
                                    id={inviteByEmailErrorId}
                                    className={styles.error}
                                    show={true}
                                    message={criticalErrors.length === 1 ? criticalErrors[0]!.getLocalizedMessage(intl) : intl.formatMessage(
                                        {id: 'invite.members.users_with_error', defaultMessage: '{count} users with error'},
                                        {count: criticalErrors.length},
                                    )}
                                />
                                {criticalErrors.length > 1 && (
                                    <Link
                                        as='button'
                                        type='button'
                                        size='medium'
                                        pseudo={true}
                                        onClick={handleDeleteInvalidEmails}
                                    >
                                        <FormattedMessage
                                            id='invite.members.delete_emails'
                                            defaultMessage='Delete'
                                        />
                                    </Link>
                                )}
                            </div>
                        )
                    }
                    {
                        criticalErrors.length === 0 && warnings.length > 0 && (
                            renderWarnings(warnings.length === 1 ? warnings[0]!.getLocalizedMessage(intl) : intl.formatMessage(
                                {id: 'invite.members.users_with_warning', defaultMessage: '{count} users with warning'},
                                {count: warnings.length},
                            ))
                        )
                    }
                    {emailsLimitExcess > 0 && (
                        <Alert
                            data-test-id='Alert'
                            size='medium'
                            className={styles.inputEmailsAlert}
                            type={AlertType.Warning}
                            message={intl.formatMessage(
                                {
                                    id: 'invite.members.overflow_limit_label',
                                    defaultMessage: 'You can invite not more than {count} users',
                                },
                                {count: emailsLimit},
                            )}
                            secondaryMessage={intl.formatMessage(
                                {
                                    id: 'invite.members.overflow_limit_secondary_label',
                                    defaultMessage: '{count} users were deleted - try calling them in a new invitation',
                                },
                                {count: emailsLimitExcess},
                            )}
                            onClose={withdrawEmailsLimitExcess}
                        />
                    )}
                    {failedToSendInvites && (
                        <Alert
                            className={styles.inputEmailsAlert}
                            size='medium'
                            type={AlertType.Danger}
                            message={intl.formatMessage({
                                id: 'invite.members.unable_to_invite_due_to_server_problems',
                                defaultMessage: 'Couldn\'t invite users due to server issues',
                            })}
                            secondaryMessage={
                                intl.formatMessage({
                                    id: 'invite.members.try_contacting_admin',
                                    defaultMessage: 'Contact support service and send invitations later',
                                })
                            }
                        >
                            <CopyEmailsButton invitationResult={result} containerId={containerId} />
                        </Alert>
                    )}
                    {rateLimitIssues.length > 0 && rateLimitIssueTimeout > 0 && (
                        <Alert
                            className={styles.inputEmailsAlert}
                            size='medium' type={AlertType.Warning}
                            message={intl.formatMessage({
                                id: 'invite.members.rate_limit_issues_v1',
                                defaultMessage: 'We can send all invitations in {minutes, plural, =1 {# minute} other {# minutes}}',
                            }, {
                                minutes: rateLimitIssueTimeout,
                            })}
                            secondaryMessage={
                                rateLimitIssues[0]!.remaininSendings! > 0 ? intl.formatMessage({
                                    id: 'invite.members.invite_users_later_v1',
                                    defaultMessage: 'Invite users later or send up to {count, plural, =1 {# email} other {# emails}} now',
                                }, {count: rateLimitIssues[0]!.remaininSendings}) : intl.formatMessage({
                                    id: 'invite.members.invite_all_users_later',
                                    defaultMessage: 'Invite users later',
                                })
                            }
                        >
                            <CopyEmailsButton invitationResult={result} containerId={containerId} />
                        </Alert>
                    )}
                </div>
                <fieldset>
                    <legend className={classNames(styles.legend, bodyMTrueTypography)}>
                        {intl.formatMessage({
                            id: 'invite.members.access_parameters',
                            defaultMessage: 'Access parameters',
                        })}
                    </legend>
                    <Controller
                        name='role'
                        control={control}
                        render={({field}) => (
                            <Select
                                readOnly={roleOptions.length <= 1}
                                data-test-id='RoleOptions'
                                options={roleOptions}
                                initialValue={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                label={intl.formatMessage({
                                    id: 'invite.members.select_role',
                                    defaultMessage: 'Role',
                                })}
                            />
                        )}
                    />

                    {isAnyTeamGuestRole(selectedRole) && (
                        <>
                            <Controller
                                name='channels'
                                control={control}
                                rules={{
                                    validate: {
                                        [ChannelsErrorType.EMPTY]: (value) => value.length > 0,
                                        [ChannelsErrorType.LIMIT_EXCEEDED]: (value) => value.length <= channelsLimit,
                                    },
                                }}
                                render={({field}) => (
                                    <InputChannels
                                        channelsMapWithError={isArchiveChannelsFoundError ? archivedChannelsMap : undefined}
                                        data-test-id='InputChannels'
                                        className={styles.inputChannels}
                                        label={
                                            intl.formatMessage({
                                                id: 'invite.members.channels_label',
                                                defaultMessage: 'Channels',
                                            })
                                        }
                                        placeholder={intl.formatMessage({
                                            id: 'invite.members.channels_placeholder',
                                            defaultMessage: 'Add up to {count} channels',
                                        }, {count: channelsLimit})}
                                        noMatchesText={intl.formatMessage({
                                            id: 'invite.members.no_matches',
                                            defaultMessage: 'No matches',
                                        })}
                                        error={Boolean(formState.errors.channels || channelError)}
                                        limit={channelsLimit}
                                        channels={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        autocompleteChannels={autocompleteChannels}
                                        aria-errormessage={channelsErrorId}
                                    />
                                )}
                            />
                            <ChannelFieldErrors
                                id={channelsErrorId}
                                role={selectedRole}
                                clientErrorCode={formState.errors.channels?.type}
                                channelsLimit={channelsLimit}
                            />
                        </>
                    )}
                    {isValidForm && generalError && (
                        <Alert
                            className={styles.inputEmailsAlert}
                            size='medium'
                            type={AlertType.Danger}
                            message={generalError.getLocalizedMessage(intl)}
                            secondaryMessage={generalError.getLocalizedSecondaryMessage(intl)}
                        />
                    )}
                </fieldset>
                <div className={styles.footer}>
                    <div data-test-id='CopyInviteLink' className={styles.copyInviteLinkWrapper}>
                        {selectedRole === 'team_user' &&
                            renderInviteMemberLink?.()
                        }
                    </div>
                    <Button data-test-id='CancelInviteByEmail' variant={BUTTON_VARIANT.TERTIARY} onPress={onCancel}>
                        {
                            intl.formatMessage({
                                id: 'invite.members.cancel',
                                defaultMessage: 'Cancel',
                            })
                        }
                    </Button>
                    <Button data-test-id='SubmitInviteByEmail' type='submit' loading={formState.isSubmitting} isDisabled={formState.isSubmitting}>
                        {
                            intl.formatMessage({
                                id: 'invite.members.send_invites',
                                defaultMessage: 'Invite',
                            })
                        }
                    </Button>
                </div>
            </form>
        );
    };
